import {
  getProductIdsBySubscriptionSkuName,
  getSubscriptionEstimatedTotal,
  normalizeSubscriptionProductLabelForMatch,
  PRIMARY_SUBSCRIPTION_PRODUCTS,
  REGULAR_DELIVERY_PRODUCT_ID,
} from 'features/order/Order.constants';

const matchesProductLabel = (candidateName, productType) => {
  if (!candidateName || !productType) return false;
  const normalizedProduct = normalizeSubscriptionProductLabelForMatch(productType);
  const normalizedCandidate = normalizeSubscriptionProductLabelForMatch(candidateName);
  if (!normalizedProduct || !normalizedCandidate) return false;
  return normalizedProduct === normalizedCandidate
    || normalizedProduct.startsWith(`${normalizedCandidate} and `)
    || normalizedProduct.includes(normalizedCandidate)
    || normalizedCandidate.includes(normalizedProduct);
};

/**
 * Resolve which primary subscription product applies to a sitemap report product type.
 * Mirrors order-submitted matching (V2 merged products, then dashboard SKU → product ids).
 */
export const findSitemapSubscriptionProduct = ({
  productType,
  isSubscriptionDashboardV2Enabled,
  primarySubscriptionProductsWithUsage,
  subscriptions,
}) => {
  if (isSubscriptionDashboardV2Enabled && primarySubscriptionProductsWithUsage?.length > 0) {
    const subscriptionProducts = primarySubscriptionProductsWithUsage.filter(
      (p) => p?.isSubscription && (Number(p.includedQty) || 0) > 0,
    );
    const matched = subscriptionProducts.find((p) => matchesProductLabel(p.name, productType));
    if (matched) return matched;
    if (subscriptionProducts.length === 1) return subscriptionProducts[0];
    return null;
  }

  if (!subscriptions?.length) return null;

  const usageByProductId = {};
  subscriptions.forEach((s) => {
    const name = (s.subscriptionSkuName || '').trim();
    const ids = getProductIdsBySubscriptionSkuName(name);
    if (!ids.length) return;
    ids.forEach((id) => {
      usageByProductId[id] = {
        usedQty: s.usedQty ?? 0,
        includedQty: s.includedQty ?? 0,
        overageRatePerUnit: s.overageRatePerUnit ?? 0,
        subscriptionSkuName: name,
      };
    });
  });

  const candidates = PRIMARY_SUBSCRIPTION_PRODUCTS
    .map((product) => {
      const usage = usageByProductId[product.productId];
      if (!usage || (Number(usage.includedQty) || 0) <= 0) return null;
      return {
        ...product,
        usedQty: usage.usedQty,
        includedQty: usage.includedQty,
        overageRatePerUnit: usage.overageRatePerUnit,
        isSubscription: true,
        unitBadgeCount: product.unitBadgeCount ?? 1,
      };
    })
    .filter(Boolean);

  const matched = candidates.find((p) => matchesProductLabel(p.name, productType));
  if (matched) return matched;
  if (candidates.length === 1) return candidates[0];
  return null;
};

/**
 * Build Usage bar props + subscription estimated cost for sitemap-submitted.
 * unitsOrdered = number of selected sitemap structures (1 structure → 1 unit base).
 * Returns null when product is not under subscription or units are invalid.
 */
export const resolveSitemapSubscriptionUsage = ({
  productType,
  structureCount,
  isSubscriptionDashboardV2Enabled,
  primarySubscriptionProductsWithUsage,
  subscriptions,
}) => {
  const pinCount = Number(structureCount) || 0;
  if (pinCount <= 0) return null;

  const subscriptionProduct = findSitemapSubscriptionProduct({
    productType,
    isSubscriptionDashboardV2Enabled,
    primarySubscriptionProductsWithUsage,
    subscriptions,
  });
  if (!subscriptionProduct?.isSubscription) return null;

  const totalUnits = Number(subscriptionProduct.includedQty) || 0;
  if (totalUnits <= 0) return null;

  const unitBadgeMultiplier = Math.max(Number(subscriptionProduct.unitBadgeCount ?? 1) || 1, 1);
  const unitsOrdered = pinCount * unitBadgeMultiplier;
  const usedBeforeOrder = Number(subscriptionProduct.usedQty) || 0;
  const usedAfterOrder = usedBeforeOrder + unitsOrdered;
  const isOverage = usedAfterOrder > totalUnits;

  const [estimatedCost] = getSubscriptionEstimatedTotal({
    subscriptionProduct,
    selectedPrimaryDeliveryProduct: REGULAR_DELIVERY_PRODUCT_ID,
    selectedOtherProducts: {},
    orderedUnits: pinCount,
  });

  return {
    unitsOrdered,
    isOverage,
    // Same as place-order / finalize: overageRatePerUnit × overage units (+ delivery/ESX).
    estimatedCost,
    // Always show Usage bar — including overage (used can exceed total; % can be > 100).
    usage: {
      planName: subscriptionProduct.name || productType,
      usedUnits: usedAfterOrder,
      totalUnits,
    },
  };
};
